<template>
    <div class="campaign-moderation">
        <div class="campaign-moderation__header">
            <h1 class="campaign-moderation__title">Модерация кампаний</h1>
            <button class="update-btn" :class="{ 'update-btn--spinning': syncing }" :disabled="syncing"
                @click="syncStatuses" aria-label="Синхронизировать статусы кампаний"></button>
        </div>

        <div v-if="campaigns.length === 0" class="campaign-moderation__empty">
            <p>Нет кампаний на модерации</p>
        </div>

        <div v-else class="campaign-moderation__list">
            <div v-for="campaign in campaigns" :key="campaign.id" class="moderation-card">
                <div class="moderation-card__header">
                    <div class="moderation-card__info">
                        <h3 class="moderation-card__title">{{ campaign.name }}</h3>
                        <div class="moderation-card__meta">
                            <span>ID: {{ campaign.hierarchical_number || campaign.id }}</span>
                            <span>Создана: {{ formatDate(campaign.created_at) }}</span>
                            <span>Период: {{ formatPeriod(campaign.period_start, campaign.period_end) }}</span>
                        </div>
                        <div v-if="campaign.created_by" class="moderation-card__creator">
                            <strong>Создатель:</strong> {{ campaign.created_by.name }}
                            <span class="muted">({{ campaign.created_by.email }}, {{ campaign.created_by.role }})</span>
                        </div>
                        <div v-if="campaign.advertiser" class="moderation-card__advertiser">
                            <strong>Рекламодатель:</strong> {{ campaign.advertiser.name }}
                        </div>
                        <div v-if="campaign.brand" class="moderation-card__brand">
                            <strong>Бренд:</strong> {{ campaign.brand.name }}
                        </div>
                        <div class="moderation-card__stats">
                            <span>Экранов: {{ campaign.frames_count }}</span>
                            <span>Общая стоимость: {{ formatPrice(campaign.total_cost) }}</span>
                        </div>
                        <div v-if="campaign.comment" class="moderation-card__comment">
                            <strong>Комментарий:</strong> {{ campaign.comment }}
                        </div>
                    </div>
                </div>

                <div class="moderation-card__actions">
                    <div class="action-buttons">
                        <a :href="campaignLink(campaign)" class="btn btn--primary">
                            Просмотр и редактирование
                        </a>
                        <template v-if="!campaign.campaign_external_id">
                            <button @click="approveCampaign(campaign)" class="btn btn--success"
                                :disabled="processingId === campaign.id">
                                {{ processingId === campaign.id ? 'Отправка...' : 'Отправить на модерацию по API' }}
                            </button>
                            <button @click="showRejectModal(campaign)" class="btn btn--danger"
                                :disabled="processingId === campaign.id">
                                Отклонить
                            </button>
                        </template>
                        <template v-else>
                            <span class="muted">Группа: откройте для модерации каждой кампании по отдельности</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Модальное окно для отклонения -->
        <div v-if="showRejectDialog" class="modal-overlay" @click.self="closeRejectModal">
            <div class="modal-content">
                <h3>Отклонить кампанию</h3>
                <p><strong>{{ campaignToReject?.name }}</strong></p>
                <div class="form-group">
                    <label for="rejection-reason">Причина отклонения (необязательно):</label>
                    <textarea id="rejection-reason" v-model="rejectionReason" class="form-control" rows="4"
                        placeholder="Укажите причину отклонения кампании..."></textarea>
                </div>
                <div class="modal-actions">
                    <button @click="closeRejectModal" class="btn btn--secondary">Отмена</button>
                    <button @click="rejectCampaign" class="btn btn--danger">Отклонить</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { formatInRtbTZ } from '../../../utils/timezones';
import { confirmAction } from '../../../composables/useConfirm';

export default {
    name: 'CampaignModeration',
    props: {
        user: {
            type: Object,
            required: true,
        },
        initialCampaigns: {
            type: Array,
            default: undefined,
        },
    },
    data() {
        return {
            campaigns: [],
            processingId: null,
            showRejectDialog: false,
            campaignToReject: null,
            rejectionReason: '',
            syncing: false,
        };
    },
    mounted() {
        this.campaigns = Array.isArray(this.initialCampaigns) ? [...this.initialCampaigns] : [];
    },
    methods: {
        async syncStatuses() {
            if (this.syncing) return;
            this.syncing = true;
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
                const res = await fetch('/api/campaigns/sync-statuses', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                if (data.success) {
                    this.$notify({
                        title: 'Успешно',
                        message: `Обновлено кампаний: ${data.campaigns_updated || 0}`,
                        type: 'success',
                    });
                }
            } catch (e) {
                this.$notify({
                    title: 'Ошибка',
                    message: 'Не удалось обновить статусы',
                    type: 'error',
                });
            } finally {
                this.syncing = false;
            }
        },
        campaignLink(campaign) {
            if (campaign.campaign_external_id) {
                return `/guaranteed/campaigns/external/${campaign.campaign_external_id}/moderation`;
            }
            return `/guaranteed/campaigns/${campaign.id}/moderation`;
        },
        async approveCampaign(campaign) {
            const isConfirmed = await confirmAction({
                title: `Одобрить кампанию «${campaign.name}»?`,
                message: 'Кампания будет отправлена по API на модерацию площадки.',
                confirmLabel: 'Одобрить',
            });
            if (!isConfirmed) return;

            this.processingId = campaign.id;
            try {
                const detailRes = await axios.get(`/api/campaigns/${campaign.id}`);
                const data = detailRes.data.campaign || {};
                const defaultApiId = data.api_id ?? '';

                const apiId = prompt('Введите api_id для отправки (ID в системе оператора):', String(defaultApiId));
                if (!apiId || !apiId.trim()) {
                    this.$notify({ title: 'Отменено', message: 'api_id обязателен для отправки', type: 'warning' });
                    this.processingId = null;
                    return;
                }

                const response = await axios.post(`/api/campaigns/${campaign.id}/approve`, {
                    api_id: parseInt(apiId.trim(), 10),
                });

                if (response.data.success) {
                    this.$notify({
                        title: response.data.success ? 'Успешно' : 'Частично успешно',
                        message: response.data.message || 'Кампания отправлена на модерацию по API',
                        type: response.data.success ? 'success' : 'warning',
                    });
                } else {
                    this.$notify({
                        title: 'Результат',
                        message: response.data.details ? JSON.stringify(response.data.details) : 'Частичный/ошибочный результат',
                        type: 'warning',
                    });
                }

                // Remove if fully successful
                if (response.data.success) {
                    this.campaigns = this.campaigns.filter(c => c.id !== campaign.id);
                }
            } catch (error) {
                this.$notify({
                    title: 'Ошибка',
                    message: error.response?.data?.error || 'Не удалось подтвердить кампанию',
                    type: 'error',
                });
            } finally {
                this.processingId = null;
            }
        },
        showRejectModal(campaign) {
            this.campaignToReject = campaign;
            this.rejectionReason = '';
            this.showRejectDialog = true;
        },
        closeRejectModal() {
            this.showRejectDialog = false;
            this.campaignToReject = null;
            this.rejectionReason = '';
        },
        async rejectCampaign() {
            if (!this.campaignToReject) return;

            this.processingId = this.campaignToReject.id;
            try {
                const response = await axios.post(`/api/campaigns/${this.campaignToReject.id}/reject`, {
                    rejection_reason: this.rejectionReason,
                });

                this.$notify({
                    title: 'Успешно',
                    message: response.data.message || 'Кампания отклонена',
                    type: 'success',
                });

                // Удаляем кампанию из списка
                this.campaigns = this.campaigns.filter(c => c.id !== this.campaignToReject.id);
                this.closeRejectModal();
            } catch (error) {
                this.$notify({
                    title: 'Ошибка',
                    message: error.response?.data?.error || 'Не удалось отклонить кампанию',
                    type: 'error',
                });
            } finally {
                this.processingId = null;
            }
        },
        formatDate(dateString) {
            return formatInRtbTZ(dateString);
        },
        formatPeriod(start, end) {
            if (!start || !end) return '—';
            const d = (str) => {
                const s = String(str).trim();
                const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (match) return `${match[3]}.${match[2]}.${match[1]}`;
                const date = new Date(s);
                if (Number.isNaN(date.getTime())) return '—';
                const dd = String(date.getDate()).padStart(2, '0');
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${dd}.${mm}.${yyyy}`;
            };
            return `${d(start)} — ${d(end)}`;
        },
        formatPrice(price) {
            if (!price) return '0 ₸';
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            }).format(price);
        },
    },
};
</script>
